import { AuthModal } from "app/features/authentication/modals/AuthModal";
import React, { createContext, useContext, useRef, useState } from "react";
import { Modal } from "react-native";
import ConfirmationModal, {
  ConfirmationModalRef,
  ConfirmOpenParams,
} from "ui/organisms/modals/ConfirmationModal";

interface ModalContextType {
  addModal: (modal: React.ReactNode) => void;
  removeModal: () => void;
  showConfirmationModal: (
    params: ConfirmOpenParams | string,
  ) => Promise<boolean>;
}

const ModalContext = createContext<ModalContextType | undefined>(undefined);

export const ModalProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [modalStack, setModalStack] = useState<React.ReactNode[]>([]);
  const confirmationModalRef = useRef<ConfirmationModalRef>(null);

  const addModal = (modal: React.ReactNode) => {
    setModalStack((prev) => [...prev, modal]);
  };

  const removeModal = () => {
    setModalStack((prev) => prev.slice(0, -1));
  };

  const showConfirmationModal = async (params: ConfirmOpenParams | string) => {
    return (await confirmationModalRef.current?.open(params)) ?? false;
  };

  return (
    <ModalContext.Provider
      value={{ addModal, removeModal, showConfirmationModal }}
    >
      {children}
      {modalStack.length > 0 ? modalStack[modalStack.length - 1] : null}
      <AuthModal />
      <ConfirmationModal ref={confirmationModalRef} />
    </ModalContext.Provider>
  );
};

export const useModalContext = () => {
  const context = useContext(ModalContext);
  if (!context) {
    throw new Error("useModalContext must be used within a ModalProvider");
  }
  return context;
};
